import copy from "copy-to-clipboard";

/**
 * Copy plain text only, deliberately offering no rich flavor.
 *
 * Some rich editors accept `text/html` but render only part of it -- Slack, for
 * instance, takes the html and flattens a table into one run-on line because it
 * has no table support. For those targets the plain flavor is the better
 * result, and the only way to guarantee it is to be the sole flavor on the
 * clipboard.
 *
 * @returns whether the write succeeded, so the caller does not report success
 *   for a clipboard that was never written.
 */
export function copyPlainText(plain: string): boolean {
  return copy(plain);
}

/**
 * Copy one payload to the clipboard in two flavors at once.
 *
 * The clipboard can hold several representations of the same content and lets
 * the paste target pick. A rich editor (Gmail's compose box, Outlook Web) takes
 * `text/html` and keeps the table structure; a plain input (Slack, a terminal)
 * falls back to `text/plain`. Copying only plain text would force the reader to
 * re-align columns by hand, which is exactly where long DKIM values get
 * mangled.
 *
 * `navigator.clipboard.write` needs a secure context and a user gesture, and it
 * is missing on older browsers, so a failure falls back to writing the plain
 * text alone rather than leaving the clipboard untouched.
 *
 * @param html - HTML representation. Style it with inline attributes only:
 *   mail clients strip `<style>` blocks and class names.
 * @param plain - Plain-text representation, used verbatim as the fallback.
 * @returns `"rich"` when both flavors were written, `"plain"` when only the
 *   plain-text fallback was, and `"failed"` when nothing reached the clipboard
 *   -- the caller must not report success in that case.
 */
export async function copyRichText(
  html: string,
  plain: string,
): Promise<"rich" | "plain" | "failed"> {
  if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      return "rich";
    } catch {
      // Permission denied, a non-secure context, or a browser that rejects the
      // html flavor -- the plain fallback below still gets the content across.
    }
  }

  return copy(plain) ? "plain" : "failed";
}
